/**
 * The keepsake's photographs — the store's answer to "does this exist?" (PRD 03 §5).
 *
 * Every other picture on the store is a rendering of an example story, and a careful visitor
 * knows it. The honest reply is not a quote from someone who says the thing is real; it is a
 * photograph of the paper. That is why this list exists and why the help text is a shooting
 * brief rather than a field description: *plain surface, daylight, no people; measure it; no
 * child's name on any page.* It stays inside the no-ratings/reviews invariant because product
 * photography is what every honest shop does.
 *
 * Files go to Storage as they are picked, like `IllustrationSlot`; the **list** is saved with
 * the rest of the form, because order, captions and the *on the home* flags are one edit. A
 * row whose file upload failed is never added, so the list never points at nothing.
 *
 * A clip carries a poster still. Without one a `<video>` is a black rectangle until it is
 * pressed, so `normalizeMedium` drops such a row and this editor says so rather than letting
 * an admin save a photograph the store will silently ignore.
 */

import { Button, Checkbox, Field, TextLink } from '@atlas-nook/design-system';
import { checkPhoto } from '@atlas-nook/firestore-store/client';
import type { Photo } from '@atlas-nook/mediums';
import { useRef, useState } from 'react';
import type { JSX } from 'react';

import { uploadPhotoFile } from '../data/writes.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { LocalizedField } from './LocalizedField.js';

type Pick = { kind: 'still' | 'clip'; slot: 'file' | 'poster'; index?: number };

export function PhotographsEditor({
  mediumId,
  rows,
  onChange,
}: {
  mediumId: string;
  rows: Photo[] | undefined;
  onChange: (next: Photo[] | undefined) => void;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<Pick | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const list = rows ?? [];

  function write(next: Photo[]): void {
    onChange(next.length === 0 ? undefined : next);
  }

  function replace(index: number, value: Photo | undefined): void {
    const next = [...list];
    if (value === undefined) next.splice(index, 1);
    else next[index] = value;
    write(next);
  }

  /** Order is meaning here — the home takes the first three — so it is editable. */
  function move(index: number, by: 1 | -1): void {
    const to = index + by;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [row] = next.splice(index, 1);
    if (row === undefined) return;
    next.splice(to, 0, row);
    write(next);
  }

  function ask(pick: Pick): void {
    setPending(pick);
    setError(null);
    // The picker is one input, retargeted: a file input per row would be one more thing to
    // label, and axe is right that an unlabelled one is critical.
    input.current?.click();
  }

  async function onPick(file: File | undefined): Promise<void> {
    const pick = pending;
    setPending(null);
    if (file === undefined || pick === null) return;

    // A poster is a still whatever the row is.
    const as = pick.slot === 'poster' ? 'still' : pick.kind;
    const refusal = checkPhoto(file, as);
    if (refusal !== null) {
      setError(
        refusal === 'wrong-type'
          ? as === 'clip'
            ? S.medium.photoWrongTypeClip
            : S.medium.photoWrongTypeStill
          : as === 'clip'
            ? S.medium.photoTooLargeClip
            : S.medium.photoTooLargeStill,
      );
      return;
    }

    setError(null);
    setWorking(true);
    try {
      const path = await uploadPhotoFile(mediumId, file);
      if (pick.index === undefined) {
        write([...list, { kind: pick.kind, path, caption: { en: '' } }]);
      } else {
        const row = list[pick.index];
        if (row !== undefined) {
          replace(pick.index, pick.slot === 'poster' ? { ...row, poster: path } : { ...row, path });
        }
      }
    } catch {
      setError(S.medium.photoFailed);
    } finally {
      setWorking(false);
    }
  }

  const accept =
    pending === null || pending.slot === 'poster' || pending.kind === 'still'
      ? 'image/png,image/jpeg,image/webp'
      : 'video/mp4,video/webm';

  return (
    <div className="stack">
      <p className="note">{S.medium.photosHelp}</p>
      {list.length === 0 && <p className="cap">{S.medium.photosNone}</p>}

      {list.map((row, index) => (
        <div key={row.path} className="stack photorow">
          <p className="cap">
            {index + 1} · {row.kind === 'clip' ? S.medium.photoClip : S.medium.photoStill}
          </p>
          {/* `.cost`, not `.cap`: a Storage path is case-sensitive and `.cap` uppercases. */}
          <p className="cost">{S.medium.photoFile(row.path)}</p>

          {row.kind === 'clip' &&
            (row.poster === undefined ? (
              <p className="note" style={{ color: 'var(--danger)' }}>
                {S.medium.photoPosterMissing}
              </p>
            ) : (
              <p className="cost">{S.medium.photoFile(row.poster)}</p>
            ))}

          <LocalizedField
            label={S.medium.photoCaption}
            help={S.medium.photoCaptionHelp}
            value={row.caption}
            required
            onChange={(caption) => replace(index, { ...row, caption: caption ?? { en: '' } })}
          />

          <Field
            label={S.medium.photoSize}
            help={S.medium.photoSizeHelp}
            value={row.size ?? ''}
            onChange={(size) => {
              const next = { ...row };
              if (size.trim() === '') delete next.size;
              else next.size = size;
              replace(index, next);
            }}
          />

          <Checkbox
            label={S.medium.photoOnHome}
            checked={row.onHome === true}
            onChange={(on) => {
              const next = { ...row };
              if (on) next.onHome = true;
              else delete next.onHome;
              replace(index, next);
            }}
          />

          <div className="btnrow">
            <TextLink onClick={() => ask({ kind: row.kind, slot: 'file', index })}>
              {S.medium.photoReplaceFile}
            </TextLink>
            {row.kind === 'clip' && (
              <TextLink onClick={() => ask({ kind: row.kind, slot: 'poster', index })}>
                {S.medium.photoPoster}
              </TextLink>
            )}
            <TextLink onClick={() => move(index, -1)}>{S.medium.photoUp(index + 1)}</TextLink>
            <TextLink onClick={() => move(index, 1)}>{S.medium.photoDown(index + 1)}</TextLink>
            <TextLink onClick={() => replace(index, undefined)}>
              {S.medium.photoRemove(index + 1)}
            </TextLink>
          </div>
        </div>
      ))}

      <p className="note">{S.medium.photoOnHomeHelp}</p>

      <div className="btnrow">
        <Button
          tone="quiet"
          size="sm"
          working={working}
          onClick={() => ask({ kind: 'still', slot: 'file' })}
        >
          {S.medium.photoAddStill}
        </Button>
        <Button
          tone="quiet"
          size="sm"
          working={working}
          onClick={() => ask({ kind: 'clip', slot: 'file' })}
        >
          {S.medium.photoAddClip}
        </Button>
      </div>

      {error !== null && (
        <p className="note" style={{ color: 'var(--danger)' }} role="alert">
          {error}
        </p>
      )}

      <input
        ref={input}
        type="file"
        className="vh"
        aria-label={
          pending?.slot === 'poster'
            ? S.medium.photoChoosePoster
            : pending?.kind === 'clip'
              ? S.medium.photoChooseClip
              : S.medium.photoChooseStill
        }
        accept={accept}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          void onPick(file);
        }}
      />
    </div>
  );
}
